import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Spinner } from 'UI';
import { Floating, getActiveLanguage, translate as translateConstructor, Button as HaldorButton } from '@haldor/ui';
import { Box, Typography, FormControlLabel, Checkbox, Stack, Chip } from "@mui/material";
import Grid from '@mui/material/Grid';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { svSE, enUS, nbNO, daDK } from '@mui/x-data-grid-pro/locales';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import Moment from 'moment';
import User from '_class/User';

import {
	deleteAbsence,
	submitAbsence,
	updateAbsence,
} from 'actions/absence';
import { getFilteredGroups, getGroupById, clearFilteredGroups } from 'actions/groups';
import { getSchoolAbsence } from 'actions/schools';
import { searchUser } from 'actions/user';
import { setPageTitle } from 'actions/header';

import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import no from 'date-fns/locale/nb';
import en from 'date-fns/locale/en-US';
import da from 'date-fns/locale/da';
import uk from 'date-fns/locale/uk';

import ReportAbsenceForm from 'containers/Forms/Absence/ReportAbsence';
import Modal from 'containers/Modals/Modal';
import Card, { CardsContainer, CardTitle } from 'components/Cards/Card';
import NoItemsFoundLayout from 'containers/Assignments/NoItemsFoundLayout';
import SelectMultiple from 'containers/Overview/TableFilterComponents/SelectMultiple';

const classes = {
	root: `root`,
	firstColumn: `firstColumn`,
	rowStyle: `rowStyle`,
};

const Root = styled('div')({
	[`&.${classes.root}`]: {
		position: 'relative',
		width: '100%',
		backgroundColor: '#FFFFFF',
		marginBottom: '3rem',
		'& .MuiDataGrid-columnHeaders': {
			backgroundColor: '#f0f0f0',
			color: '#333',
			fontSize: '1rem',
			fontWeight: 'bold !important',
			cursor: 'unset !important',
		},
		'& .MuiDataGrid-columnHeaderTitle': {
			fontWeight: 'normal !important',
			whiteSpace: 'normal',
			lineHeight: '1.2',
			overflow: 'visible',
		},
	},
	[`& .${classes.firstColumn}`]: {
		boxShadow: '5px 0 10px #0000000d',
		fontSize: '.688rem',
	},
	[`& .${classes.rowStyle}`]: {
		cursor: 'pointer',
	},
});

const localeMapDataGrid = {
	'EN-US': enUS,
	'SV-SE': svSE,
	'NB-NO': nbNO,
	'DA-DK': daDK,
};

registerLocale('en-US', en);
registerLocale('sv-SE', sv);
registerLocale('nb-NO', no);
registerLocale('da-DK', da);
registerLocale('uk', uk);
setDefaultLocale('en-US');

const Absences = ({ }) => {

	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const apiRef = useGridApiRef();
	const [date, setDate] = useState(Moment());
	const [isAdmin, setAdmin] = useState(null);
	const [loading, setLoading] = useState(true);
	const [datagridLanguage, setDatagridLanguage] = useState(enUS);
	const [absenceModalOpen, setAbsenceModalOpen] = useState(false);
	const [selectedStudent, setSelectedStudent] = useState(null);
	const [checkedGroupIds, setCheckedGroupIds] = useState([]);
	const [isFetchingDetails, setIsFetchingDetails] = useState(false);
	const [searching, setsearching] = useState(false);
	const [searchString, setsearchString] = useState(null);
	const [currentSelectedTab, setCurrentSelectedTab] = useState("#absences");
	// Central list of supported tab hashes
	const ALLOWED_TABS = useMemo(() => ['#absences', '#report-absence'], []);
	const schoolAbsences = useSelector(state => state.School.absence);
	const mentorGroups = useSelector(state => state.sectionsV2.filteredGroups.sort((a, b) => a.title.localeCompare(b.title)));
	const studentSearchResults = useSelector(state => state.user.searchResults);
	const currentUser = useSelector(state => state.user.currentUser);
	const translations = useSelector(state => state.Languages.translations);
	const translate = useMemo(() => translateConstructor(translations), [translations]);

	const columns = [
		{
			field: 'student',
			headerName: translate('Student'),
			flex: 1,
			sortable: true,
			disableColumnMenu: true,
			sortComparator: (v1, v2) => {
				if (!v1 && !v2) return 0;
				if (!v1) return -1;
				if (!v2) return 1;

				const firstName1 = (v1.firstName || '').toLowerCase();
				const firstName2 = (v2.firstName || '').toLowerCase();
				const lastName1 = (v1.lastName || '').toLowerCase();
				const lastName2 = (v2.lastName || '').toLowerCase();

				const firstNameComparison = firstName1.localeCompare(firstName2, 'sv');
				if (firstNameComparison !== 0) return firstNameComparison;

				return lastName1.localeCompare(lastName2, 'sv');
			},
			renderCell: (params) => {
				const student = params.row.student;
				if (!student) return '';
				const showDot = params.row?.hasReportedAbsence === false;

				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						{showDot && (
							<Tooltip title={translate('Absence report missing')} arrow>
								<span
									style={{
										width: 10,
										height: 10,
										borderRadius: '50%',
										backgroundColor: 'orange',
										display: 'inline-block',
										marginRight: 8,
										cursor: 'default',
									}}
								/>
							</Tooltip>
						)}
						{`${student.firstName ?? ''} ${student.lastName ?? ''}`}
					</div>
				);
			},
		},
		{
			field: 'email',
			headerName: translate('e-mail'),
			flex: 1,
			sortable: true,
			disableColumnMenu: true,
		},
		{
			field: 'start',
			headerName: translate('start-time'),
			renderCell: (params) => {
				return params.value ? new Date(params.value + 'Z').toLocaleString([], {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit'
				}) : ''
			},
			flex: 1,
			sortable: true,
			disableColumnMenu: true,
		},
		{
			field: 'end',
			headerName: translate('end-time'),
			renderCell: (params) => {
				return params.value ? new Date(params.value + 'Z').toLocaleString([], {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit'
				}) : ''
			},
			flex: 1,
			sortable: true,
			disableColumnMenu: true,
		},
		{
			field: 'mentorGroups',
			headerName: translate('mentor-header-overview'),
			flex: 2,
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params) => {
				const groups = params.row.mentorGroups || [];
				return (
					<Stack direction="column" spacing={0.5} flexWrap="wrap">
						{groups.map((g) => (
							<Typography key={g.id} variant="body2">
								{g.title}
							</Typography>
						))}
					</Stack>
				);
			},
		}
	];

	useEffect(() => {
		dispatch(setPageTitle(translate('absence-today')));
	}, [setPageTitle, translate]);

	// Initialize tab from current location hash
	useEffect(() => {
		const initialHash = location.hash;
		if (ALLOWED_TABS.includes(initialHash) && initialHash !== currentSelectedTab) {
			setCurrentSelectedTab(initialHash);
		} else if (!initialHash || !ALLOWED_TABS.includes(initialHash)) {
			// Push default tab hash without adding duplicate entries (replace current)
			history.replace({ pathname: location.pathname, search: location.search, hash: currentSelectedTab });
		}
	}, []);

	// React to history location changes (back/forward) for hash changes
	useEffect(() => {
		const unlisten = history.listen((loc) => {
			if (ALLOWED_TABS.includes(loc.hash) && loc.hash !== currentSelectedTab) {
				setCurrentSelectedTab(loc.hash);
			}
		});
		return unlisten;
	}, [history, ALLOWED_TABS, currentSelectedTab]);

	useEffect(() => {
		if (currentUser != null && currentUser.lcid != null && localeMapDataGrid[currentUser.lcid.toUpperCase()] != null) {
			setDatagridLanguage(localeMapDataGrid[currentUser.lcid.toUpperCase()]);
		}
	}, [currentUser]);

	useEffect(() => {
		if (!currentUser) return;
		fetchAbsences(undefined, true);
	}, []);

	useEffect(() => {
		if (!schoolAbsences) return;
		const userIds = [...new Set(schoolAbsences.map(a => a.user?.userId).filter(Boolean))];
		fetchFilteredMentorGroups(userIds);
	}, [schoolAbsences]);

	const fetchAbsences = async (targetDate = date, force = false) => {
		if (!force && targetDate.isSame(date, 'day')) return;
		const userInstance = new User(currentUser);
		const isAdmin = userInstance.isAdministrator();
		setAdmin(isAdmin);
		if (!isAdmin) return;

		await dispatch(getSchoolAbsence(
			userInstance.getActiveSchool().id,
			encodeURIComponent(targetDate.format())
		));
		setDate(targetDate);
		setLoading(false);
	};

	const fetchFilteredMentorGroups = async (userIds) => {
		const payload = {
			userIds,
			query: '',
			filter: 'MENTOR_GROUP;ACTIVE',
			sortBy: '',
			pageIndex: null,
			pageSize: null,
			expand: ['MEMBERS']
		};
		dispatch(clearFilteredGroups());
		if (userIds.length > 0) {
			setLoading(true);
			await dispatch(getFilteredGroups(payload));
			setLoading(false);
		}
	};

	const rows = useMemo(() => {
		if (!schoolAbsences || !mentorGroups) return [];

		const grouped = new Map();
		for (const absence of schoolAbsences) {
			const userId = absence.user?.userId;
			if (!userId) continue;

			const startTime = new Date(absence.startTime);
			const endTime = new Date(absence.endTime);

			const existing = grouped.get(userId);
			if (!existing) {
				grouped.set(userId, {
					id: userId,
					student: absence.user,
					email: absence.user?.email ?? '',
					start: startTime,
					end: endTime,
					allAbsences: [absence],
				});
			} else {
				existing.start = startTime < existing.start ? startTime : existing.start;
				existing.end = endTime > existing.end ? endTime : existing.end;
				existing.allAbsences.push(absence);
			}
		}

		const entries = Array.from(grouped.values()).map((entry) => {
			const attendanceAbsences = entry.allAbsences.filter(a => a.origin === 'ATTENDANCE');
			const reportedAbsences = entry.allAbsences.filter(a => a.origin !== 'ATTENDANCE');

			let hasReportedAbsence = true;
			for (const att of attendanceAbsences) {
				const attStart = new Date(att.startTime);
				const attEnd = new Date(att.endTime);

				const overlaps = reportedAbsences.some(rep => {
					const repStart = new Date(rep.startTime);
					const repEnd = new Date(rep.endTime);
					return attStart >= repStart && attEnd <= repEnd;
				});

				if (!overlaps) {
					hasReportedAbsence = false;
					break;
				}
			}

			const groups = mentorGroups.filter(group =>
				group.members?.some(member => member.userId === entry.id && !group.archived)
			);

			return {
				id: entry.id,
				student: entry.student,
				email: entry.email,
				start: entry.start,
				end: entry.end,
				hasReportedAbsence,
				mentorGroups: groups,
			};
		});

		if (checkedGroupIds.length > 0) {
			return entries.filter(entry =>
				entry.mentorGroups.some(group => checkedGroupIds.includes(group.id))
			);
		}

		return entries;
	}, [schoolAbsences, mentorGroups, checkedGroupIds]);

	const onMentorGroupChecked = async (groupId) => {
		if (isFetchingDetails) return;

		const alreadyChecked = checkedGroupIds.includes(groupId);
		const updatedChecked = alreadyChecked
			? checkedGroupIds.filter(id => id !== groupId)
			: [...checkedGroupIds, groupId];

		setCheckedGroupIds(updatedChecked);
		if (!alreadyChecked) {
			setIsFetchingDetails(true);
			try {
				await dispatch(getGroupById(groupId, ['members']));
			} finally {
				setIsFetchingDetails(false);
			}
		}
	};

	const onTabChange = (event, nextTab) => {
		if (nextTab === currentSelectedTab) return;
		setCurrentSelectedTab(nextTab);
		// Create a new history entry with updated hash so back button works
		history.push({ pathname: location.pathname, search: location.search, hash: nextTab });
	};

	const getDateFormat = () => {
		let format = Moment.localeData(getActiveLanguage()).longDateFormat("L");
		let newFormat = format.replace("YYYY", "yyyy");
		let days = newFormat.replace("DD", "dd");
		return days;
	};

	const getMomentDateFormat = () => {
		return Moment.localeData(getActiveLanguage()).longDateFormat("L");
	};

	const normalizeLocale = (locale) => {
		const [language, region] = locale.split('-');

		if (language.toUpperCase() == "UK") {
			return `uk`;
		}
		return `${language}-${region.toUpperCase()}`;
	};

	const onDateChange = (date, event) => {
		const momentDate = Moment(date);
		if (!momentDate.isValid()) return;

		setCheckedGroupIds([]);
		if (event?.type !== "change") {
			fetchAbsences(momentDate);
		}
	}

	const handleBlur = (e) => {
		const typedDate = Moment(e.target.value, getMomentDateFormat(), true);
		if (!loading && typedDate.isValid()) {
			fetchAbsences(typedDate);
		}
	};

	const handleRowClick = (params) => {
		setSelectedStudent(params.row.student);
		setAbsenceModalOpen(true);
	};
	const onStudentSearchResultsClick = (student) => {
		setSelectedStudent(student);
		setAbsenceModalOpen(true);
	};
	const onModalClose = (params) => {
		setSelectedStudent(null);
		setAbsenceModalOpen(false);
	};

	const onStudentSearch = async (event) => {
		event.preventDefault();
		if (searchString.length < 3) {
			return false;
		}
		setsearching(true);
		await dispatch(searchUser(searchString));
		setsearching(false);
	};

	const onAbsenceSubmit = (values) => {
		return new Promise((resolve, reject) => {
			let data = [values];

			if (values.id != null) {
				dispatch(updateAbsence(data)).then(() => {
					resolve(1);
					fetchAbsences();
				}).catch(() => {
					reject(0);
				})
			} else {
				dispatch(submitAbsence(data)).then(() => {
					resolve(1);
					fetchAbsences();
				}).catch(() => {
					reject(0);
				});
			}
		});
	};

	const onDelete = (id) => {
		dispatch(deleteAbsence(id)).then(() => {
			onModalClose();
		})
	};

	const renderAbsencesLayout = (groupid) => {
		return (
			<div>
				<Grid item xs={12} sm={12} md={9} >
					<Grid container spacing={2}>
						<Grid item xs={12} sm={8} md={10}>
							<Box sx={{ marginBottom: 1 }}>
								<Typography variant="body1">
									<strong>{translate('select-date')}</strong>
								</Typography>
							</Box>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={8}>
									<Box sx={{ height: '100%', width: '100%' }}>
										<DatePicker
											selected={date.toDate()}
											dateFormat={getDateFormat()}
											onChange={onDateChange}
											onBlur={handleBlur}
											popperModifiers={{
												preventOverflow: {
													enabled: true,
												},
												positionFixed: false,
											}}
											popperPlacement="bottom-start"
											showWeekNumbers
											fixedHeight
											previousMonthButtonLabel=""
											nextMonthButtonLabel=""
											customInput={
												<input
													style={{
														height: '40px',
														width: '100%',
														padding: '0 10px',
														boxSizing: 'border-box',
														borderRadius: 4,
														border: '1px solid #c9c9c9',
														fontSize: '1rem',
													}}
												/>
											}
											locale={normalizeLocale(getActiveLanguage())}
											{...date}
										/>
									</Box>
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12} sm={4} md={2}>
							<Box sx={{ marginBottom: 1 }}>
								<Typography variant="body1">
									<strong>{translate('Mentor groups')}</strong>
								</Typography>
							</Box>
							<SelectMultiple
								options={mentorGroups}
								label={checkedGroupIds?.length > 0 ?
									`${translate('Showing')} ${checkedGroupIds.length} ${translate('of')} ${mentorGroups.length}` :
									translate('select-an-alternative')
								}
								onChange={(selected) => setCheckedGroupIds(selected.map(item => item.id))}
								initialSelectedItems={mentorGroups.filter(group => checkedGroupIds.includes(group.id))}
								sx={{
									minWidth: 300,
									maxWidth: 350,
									borderRadius: '5px',
									backgroundColor: '#fff',
								}}
								disable={mentorGroups.length == 0}
							/>
						</Grid>
					</Grid>
					<Grid item xs={12} sx={{ marginTop: 3, marginBottom: 4 }}>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<Box >
									<Typography variant="body1">
										{translate('Reported as absent from the lesson')}: <strong>{rows.length}</strong>{' '}
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={12} sm={6}>
								<Box >
									<Typography variant="body1">
										{translate('Reported as absent')}: <strong>{rows.filter(row => row.hasReportedAbsence).length}</strong>
									</Typography>
								</Box>
							</Grid>
						</Grid>
					</Grid>

				</Grid>

				<Root className={classes.root}>
					<DataGridPro
						sx={{
							width: '100%',
							minHeight: '400px',
							[`& .${gridClasses.cell}`]: {
								display: 'flex',
								alignItems: 'center',
								lineHeight: '1.0'
							},
						}}
						apiRef={apiRef}
						loading={loading}
						disableColumnMenu
						slotProps={{
							loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
						}}
						slots={{
							noRowsOverlay: () => <NoItemsFoundLayout title="No search results" />,
						}}
						localeText={datagridLanguage.components.MuiDataGrid.defaultProps.localeText}
						pagination
						paginationMode="client"
						pageSizeOptions={[50, 100]}
						initialState={{
							sorting: {
								sortModel: [{ field: 'student', sort: 'asc' }],
							},
							pagination: {
								paginationModel: {
									pageSize: 100,
								},
							},
						}}
						disableMultipleColumnsSorting
						checkboxSelection={false}
						sortingOrder={['asc', 'desc']}
						disableColumnResize={false}
						disableColumnReorder
						disableColumnSelector
						rows={rows}
						columns={columns}
						getRowHeight={(params) => {
							var size = params.model.mentorGroups.length > 1 ? params.model.mentorGroups.length * 27 : 55;
							return size;
						}}
						onRowClick={(params) => handleRowClick(params)}
					/>
				</Root>
			</div >
		);
	}
	const renderReportAbsencesLayout = (groupid) => {
		return (
			<div>
				<Grid item xs={12} sm={12} md={9} >
					<Grid container spacing={2}>
						<Grid item xs={12} sm={8} md={10}>
							<Box sx={{ marginBottom: 1 }}>
								<Typography variant="body1">
									<strong>{translate('search-student')}</strong>
								</Typography>
							</Box>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={8}>
									<Box sx={{ height: '100%', width: '100%' }}>
										<input
											type="text"
											value={searchString}
											onChange={e => setsearchString(e.target.value)}
											style={{
												backgroundColor: '#fff',
												color: '#000',
												border: '1px solid #c9c9c9',
												width: '100%',
												font: 'Open Sans,Arial',
												padding: '15px',
												fontSize: '1em',
												fontWeight: '400',
												minHeight: '40px',
												appearance: 'textfield',
												height: '40px'
											}}
											placeholder={translate('search') + '...'}
										/>
									</Box>
								</Grid>
								<Grid item xs={12} sm={4}>
									<HaldorButton
										onClick={onStudentSearch}
										fullWidth
										disabled={searchString?.length < 3}
										style={{
											minWidth: '80px',
											height: '100%',
											backgroundColor: '#1976d2',
											color: '#fff',
											border: 'none',
											borderRadius: '4px',
											padding: '8px 16px',
										}}
									>
										{loading ? <Spinner center /> : null}
										{translate('search')}
									</HaldorButton>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<div style={{ marginTop: '2rem' }}>
					<CardsContainer>
						{searching ?
							<Spinner center />
							: studentSearchResults?.length > 0 ?
								studentSearchResults.map((student) => (
									<Card
										type="group"
										id={student.userId}
										key={`student-${student.userId}`}
										onClick={e => onStudentSearchResultsClick(student)}
									>
										<span className="color--meta size-12">
											{student.email}
										</span>

										<CardTitle>
											{student.firstName} {student.lastName}
										</CardTitle>
									</Card>
								)) :
								searchString && studentSearchResults?.length == 0 ?
									<div className="color--meta text--center weight--bold x">
										{translate('no-results')}
									</div>
									: null}
					</CardsContainer>
				</div>
			</div>
		);
	}

	const renderSelectedTab = (currentSelectedTab) => {
		switch (currentSelectedTab) {
			case "#absences":
				return renderAbsencesLayout();
			case "#report-absence":
				return renderReportAbsencesLayout();
			default:
				return null;
		}
	}

	return (
		<div style={{ padding: '10px' }}>
			<Modal isOpen={absenceModalOpen} onClose={onModalClose} title={`${translate('manage-absence-for')} ${selectedStudent?.firstName ?? ''} ${selectedStudent?.lastName ?? ''}`.trim()}>
				<ReportAbsenceForm
					onModalClose={onModalClose}
					onSubmit={onAbsenceSubmit}
					user={selectedStudent}
					startUtc={currentSelectedTab === "#absences" ? date.clone().startOf('day').utc().toISOString() : null}
					endUtc={currentSelectedTab === "#absences" ? date.clone().endOf('day').utc().toISOString() : null}
					absence={null}
					onDelete={onDelete}
				/>
			</Modal>
			<Grid container direction="row" justifyContent="space-between" spacing={2} sx={{ marginBottom: 4 }}>
				<Grid item size={{ xs: 12, md: 7 }}>
					<TabContext value={currentSelectedTab}>
						<TabList onChange={(event, newValue) => onTabChange(event, newValue)}>
							<Tab label={translate('Show absence')} value="#absences" sx={{ textTransform: 'none', fontSize: '1.17em' }} />
							<Tab label={translate('report-absence-for-student')} value="#report-absence" sx={{ textTransform: 'none', fontSize: '1.17em' }} />
						</TabList>
					</TabContext>
				</Grid>
			</Grid>

			{renderSelectedTab(currentSelectedTab)}
		</div >
	);
};

export default Absences;
